import React from "react";

import { Error } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { UrlParamTypes } from "../../../../types/org-display";
import { useTrainingMigrationContext } from "./context/training-migration-provider";

export const StartTrainingMigrationForm: React.FC = () => {
  const theme = useTheme();
  const { roleId } = useParams<UrlParamTypes>();
  const { dataMigrationFormState, setDataMigrationFormState } =
    useTrainingMigrationContext();

  return (
    <Grid container paddingTop={2}>
      <Grid item xs={12} container direction="column">
        <Box
          bgcolor={theme.palette.warning.main}
          borderRadius="5px"
          border={1}
          borderColor={theme.palette.warning.main}
          data-testid="migrate-training-info-panel"
        >
          <Grid container alignItems="center">
            <Grid item xs={2} container justifyContent="center">
              <Error fontSize="large" />
            </Grid>
            <Grid item xs={10} style={{ paddingRight: "20px" }}>
              <ol>
                <li>
                  <Typography>
                    You are about to start a Training Data Migration.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    This process will take some time depending on the size of
                    the organisation and the amount of Training data.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <b>Target region</b> must be provided.
                  </Typography>
                </li>
              </ol>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel required disabled>
            Organisation ID
          </FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Organisation ID"
            inputProps={{
              "data-testid": "input-organisation-id",
            }}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            value={roleId}
          />
        </Grid>
      </Grid>

      <SourceTargetFields />

      <Grid container>
        <Grid item xs={5}>
          Enable Grace Period
        </Grid>
        <Grid item xs={7}>
          <Checkbox
            checked={dataMigrationFormState.enableGracePeriod}
            onChange={(e) =>
              setDataMigrationFormState({
                ...dataMigrationFormState,
                enableGracePeriod: e.target.checked,
              })
            }
            data-testid="input-enable-grace-period"
            inputProps={{ "aria-label": "controlled" }}
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={5}>
          Activity Count
        </Grid>
        <Grid item xs={7}>
          <TextField
            name="Activity count"
            inputProps={{ "data-testid": "input-activity-count" }}
            fullWidth
            margin="normal"
            variant="outlined"
            value={dataMigrationFormState.activityCount}
            type="number"
            onChange={(e) =>
              setDataMigrationFormState({
                ...dataMigrationFormState,
                activityCount: Number(e.target.value),
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={5}>
          Worker Count
        </Grid>
        <Grid item xs={7}>
          <TextField
            name="Worker count"
            inputProps={{ "data-testid": "input-worker-count" }}
            fullWidth
            margin="normal"
            variant="outlined"
            value={dataMigrationFormState.workerCount}
            type="number"
            onChange={(e) =>
              setDataMigrationFormState({
                ...dataMigrationFormState,
                workerCount: Number(e.target.value),
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={5}>
          Map Activity Count
        </Grid>
        <Grid item xs={7}>
          <TextField
            name="Map Activity Count"
            inputProps={{ "data-testid": "input-map-activity-count" }}
            fullWidth
            margin="normal"
            variant="outlined"
            value={dataMigrationFormState.mapActivityCount}
            type="number"
            onChange={(e) =>
              setDataMigrationFormState({
                ...dataMigrationFormState,
                mapActivityCount: Number(e.target.value),
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={5}>
          Enable Bulk Processing
        </Grid>
        <Grid item xs={7}>
          <Checkbox
            checked={dataMigrationFormState.enableBulkProcessing}
            onChange={(e) =>
              setDataMigrationFormState({
                ...dataMigrationFormState,
                enableBulkProcessing: e.target.checked,
              })
            }
            data-testid="input-enable-bulk-processing"
            inputProps={{
              "aria-label":
                "Enable bulk processing for training data migration",
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const SourceTargetFields: React.FC = () => {
  const { dataMigrationFormState, setDataMigrationFormState } =
    useTrainingMigrationContext();

  return (
    <>
      <Grid container direction="row" alignItems="center">
        <Grid item xs={9}>
          <FormLabel required disabled>
            Source Region
          </FormLabel>
        </Grid>

        <Grid item xs={3}>
          <TextField
            name="Source Region"
            placeholder="AU, US, EU"
            inputProps={{
              "data-testid": "input-source-region",
            }}
            fullWidth
            margin="normal"
            variant="outlined"
            onChange={(e) =>
              setDataMigrationFormState({
                ...dataMigrationFormState,
                sourceRegion: e.target.value,
              })
            }
            value={dataMigrationFormState.sourceRegion}
          />
        </Grid>
      </Grid>

      <FormControl fullWidth>
        <Grid container direction="row" alignItems="center" paddingTop={1}>
          <Grid item xs={9}>
            <FormLabel required>Target Region</FormLabel>
          </Grid>
          <Grid item xs={3}>
            <TextField
              name="Target Region"
              placeholder="AU, US, EU"
              inputProps={{
                "data-testid": "input-target-region",
              }}
              error={dataMigrationFormState.targetRegion === ""}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) =>
                setDataMigrationFormState({
                  ...dataMigrationFormState,
                  targetRegion: e.target.value,
                })
              }
              value={dataMigrationFormState.targetRegion}
            />
          </Grid>
        </Grid>
      </FormControl>
    </>
  );
};
